import { useState, useEffect } from 'react'
import { OverlayPortal } from '../../components/ModalPortal'
import { useNavigate } from 'react-router-dom'
import { ClipboardList, X, Check } from 'lucide-react'
import { useI18n } from '../../i18n'
import { useProfileStore } from '../../stores'
import ProfileSubpageLayout from '../../components/ProfileSubpageLayout'
import SearchableChecklist from '../../components/SearchableChecklist'
import { MEDICAL_CONDITIONS, SURGERIES } from '../../data/medicalLists'
// ★ TEST 1.03 — this screen was one of the three sites that ALREADY treated a non-array
//   `conditions` as a thing that happens. Its inline ternary is now the shared coercion,
//   so the app says this one way rather than three.
import { toConditionList } from '../../utils/profileConditions'

export default function ProfileMedicalHistory() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const { profile, updateProfile, syncSection } = useProfileStore()
  
  const [noConditions, setNoConditions] = useState(false)
  const [selectedConditions, setSelectedConditions] = useState([])
  const [expandedCondition, setExpandedCondition] = useState(null)
  const [noSurgeries, setNoSurgeries] = useState(false)
  const [selectedSurgeries, setSelectedSurgeries] = useState([])
  const [expandedSurgery, setExpandedSurgery] = useState(null)
  const [showConditionModal, setShowConditionModal] = useState(false)
  const [showSurgeryModal, setShowSurgeryModal] = useState(false)
  const [newItem, setNewItem] = useState({ name: '', year: '' })

  useEffect(() => {
    if (profile?.medicalHistory) {
      const mh = profile.medicalHistory
      if (mh.noConditions) setNoConditions(true)
      if (mh.conditions) {
        const mapped = toConditionList(mh.conditions)
        setSelectedConditions(mapped)
        if (mapped.length > 0) setNoConditions(false)
      }
      if (mh.noSurgeries) setNoSurgeries(true)
      if (mh.surgeries) {
        const mapped = Array.isArray(mh.surgeries) ? mh.surgeries.map(s => typeof s === 'string' ? { id: `custom-${s}`, name: s, custom: true } : s) : []
        setSelectedSurgeries(mapped)
        if (mapped.length > 0) setNoSurgeries(false)
      }
    }
  }, [profile])

  const handleSave = async () => {
    const data = { conditions: selectedConditions, noConditions, surgeries: selectedSurgeries, noSurgeries, completed: true }
    const result = await syncSection('medicalHistory', data)
    if (!result.success) updateProfile({ medicalHistory: data })
    navigate('/profile')
  }

  const toggleCondition = (item) => {
    const existing = selectedConditions.find(c => c.id === item.id)
    if (existing) { setSelectedConditions(selectedConditions.filter(c => c.id !== item.id)); setExpandedCondition(null) }
    else { setSelectedConditions([...selectedConditions, { ...item, year: '' }]); setExpandedCondition(item.id); setNoConditions(false) }
  }

  const toggleSurgery = (item) => {
    const existing = selectedSurgeries.find(s => s.id === item.id)
    if (existing) { setSelectedSurgeries(selectedSurgeries.filter(s => s.id !== item.id)); setExpandedSurgery(null) }
    else { setSelectedSurgeries([...selectedSurgeries, { ...item, year: '' }]); setExpandedSurgery(item.id); setNoSurgeries(false) }
  }

  const handleAddCustomCondition = () => {
    if (newItem.name.trim()) { setSelectedConditions([...selectedConditions, { id: `custom-${Date.now()}`, name: newItem.name.trim(), year: newItem.year, custom: true }]); setNewItem({ name: '', year: '' }); setShowConditionModal(false); setNoConditions(false) }
  }
  const handleAddCustomSurgery = () => {
    if (newItem.name.trim()) { setSelectedSurgeries([...selectedSurgeries, { id: `custom-${Date.now()}`, name: newItem.name.trim(), year: newItem.year, custom: true }]); setNewItem({ name: '', year: '' }); setShowSurgeryModal(false); setNoSurgeries(false) }
  }

  const yearOptions = Array.from({ length: 80 }, (_, i) => new Date().getFullYear() - i)
  const inputCls = "w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9] bg-white transition-colors"

  const renderConditionExpanded = (item) => (
    <div className="mt-2">
      <label className="block text-[11px] text-gray-500 mb-1">{t('profileMedHistory.yearDiagnosed')}</label>
      <select value={item.year || ''} onChange={(e) => setSelectedConditions(selectedConditions.map(c => c.id === item.id ? { ...c, year: e.target.value } : c))}
        className={inputCls} style={{ color: '#15354F' }}>
        <option value="">{t('profileMedHistory.selectYear')}</option>
        {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  )

  const renderSurgeryExpanded = (item) => (
    <div className="mt-2">
      <label className="block text-[11px] text-gray-500 mb-1">{t('profileMedHistory.yearPerformed')}</label>
      <select value={item.year || ''} onChange={(e) => setSelectedSurgeries(selectedSurgeries.map(s => s.id === item.id ? { ...s, year: e.target.value } : s))}
        className={inputCls} style={{ color: '#15354F' }}>
        <option value="">{t('profileMedHistory.selectYear')}</option>
        {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  )

  return (
    <>
      <ProfileSubpageLayout sectionId="medical-history" icon={ClipboardList} onSave={handleSave} saving={false} canSave={true}>
        {/* Conditions */}
        <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileMedHistory.pastMedicalConditions')}</p>

        <button onClick={() => { const v = !noConditions; setNoConditions(v); if (v) setSelectedConditions([]) }}
          className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-[14px] border-[1.5px] mb-3 transition-colors ${noConditions ? 'border-[#46A8F9] bg-[#E9F8FF]' : 'border-gray-200'}`}>
          <div className={`w-5 h-5 rounded-full border-[1.5px] flex items-center justify-center ${noConditions ? 'border-[#46A8F9] bg-[#46A8F9]' : 'border-gray-300'}`}>
            {noConditions && <Check size={12} className="text-white" />}
          </div>
          <span className={`text-[13px] ${noConditions ? 'font-semibold' : 'text-gray-600 font-medium'}`} style={noConditions ? { color: '#15354F' } : {}}>{t('profileMedHistory.noPriorMedicalConditions')}</span>
        </button>

        {!noConditions && (
          <SearchableChecklist
            items={MEDICAL_CONDITIONS}
            selectedItems={selectedConditions}
            onToggle={toggleCondition}
            onRemoveCustom={(id) => setSelectedConditions(selectedConditions.filter(c => c.id !== id))}
            onAddCustom={() => { setNewItem({ name: '', year: '' }); setShowConditionModal(true) }}
            addLabel="Add other condition"
            placeholder={t('profileMedHistory.searchConditions')}
            renderExpanded={renderConditionExpanded}
            expandedId={expandedCondition}
            onExpand={setExpandedCondition}
          />
        )}

        <div className="border-t border-gray-100 my-5" />

        {/* Surgeries */}
        <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileMedHistory.pastSurgeries')}</p>

        <button onClick={() => { const v = !noSurgeries; setNoSurgeries(v); if (v) setSelectedSurgeries([]) }}
          className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-[14px] border-[1.5px] mb-3 transition-colors ${noSurgeries ? 'border-[#46A8F9] bg-[#E9F8FF]' : 'border-gray-200'}`}>
          <div className={`w-5 h-5 rounded-full border-[1.5px] flex items-center justify-center ${noSurgeries ? 'border-[#46A8F9] bg-[#46A8F9]' : 'border-gray-300'}`}>
            {noSurgeries && <Check size={12} className="text-white" />}
          </div>
          <span className={`text-[13px] ${noSurgeries ? 'font-semibold' : 'text-gray-600 font-medium'}`} style={noSurgeries ? { color: '#15354F' } : {}}>{t('profileMedHistory.noPriorSurgeries')}</span>
        </button>

        {!noSurgeries && (
          <SearchableChecklist
            items={SURGERIES}
            selectedItems={selectedSurgeries}
            onToggle={toggleSurgery}
            onRemoveCustom={(id) => setSelectedSurgeries(selectedSurgeries.filter(s => s.id !== id))}
            onAddCustom={() => { setNewItem({ name: '', year: '' }); setShowSurgeryModal(true) }}
            addLabel="Add other surgery"
            placeholder={t('profileMedHistory.searchSurgeries')}
            renderExpanded={renderSurgeryExpanded}
            expandedId={expandedSurgery}
            onExpand={setExpandedSurgery}
          />
        )}
      </ProfileSubpageLayout>

      {/* Add Condition Modal */}
      {showConditionModal && (
        <OverlayPortal>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[16px] font-bold" style={{ color: '#15354F' }}>{t('profileMedHistory.addCondition')}</h3>
              <button onClick={() => setShowConditionModal(false)}><X className="text-gray-400" size={22} /></button>
            </div>
            <div className="space-y-4">
              <div><label className="block text-[12px] text-gray-500 mb-1">{t('profileMedHistory.conditionName')}</label>
                <input type="text" value={newItem.name} onChange={(e) => setNewItem({...newItem, name: e.target.value})} placeholder={t('profileMedHistory.conditionPlaceholder')} className={inputCls} style={{ color: '#15354F' }} autoFocus /></div>
              <div><label className="block text-[12px] text-gray-500 mb-1">{t('profileMedHistory.yearDiagnosed')}</label>
                <select value={newItem.year} onChange={(e) => setNewItem({...newItem, year: e.target.value})} className={inputCls} style={{ color: '#15354F' }}>
                  <option value="">{t('profileMedHistory.selectYear')}</option>{yearOptions.map(y => <option key={y} value={y}>{y}</option>)}</select></div>
              <button onClick={handleAddCustomCondition} disabled={!newItem.name.trim()}
                className={`w-full py-3 rounded-2xl font-bold text-[14px] ${newItem.name.trim() ? 'text-white' : 'bg-gray-200 text-gray-400'}`} style={newItem.name.trim() ? { background: '#15354F' } : {}}>{t('profileMedHistory.add')}</button>
            </div>
          </div>
        </div>
        </OverlayPortal>
      )}

      {/* Add Surgery Modal */}
      {showSurgeryModal && (
        <OverlayPortal>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[16px] font-bold" style={{ color: '#15354F' }}>{t('profileMedHistory.addSurgery')}</h3>
              <button onClick={() => setShowSurgeryModal(false)}><X className="text-gray-400" size={22} /></button>
            </div>
            <div className="space-y-4">
              <div><label className="block text-[12px] text-gray-500 mb-1">{t('profileMedHistory.surgeryName')}</label>
                <input type="text" value={newItem.name} onChange={(e) => setNewItem({...newItem, name: e.target.value})} placeholder={t('profileMedHistory.surgeryPlaceholder')} className={inputCls} style={{ color: '#15354F' }} autoFocus /></div>
              <div><label className="block text-[12px] text-gray-500 mb-1">{t('profileMedHistory.yearPerformed')}</label>
                <select value={newItem.year} onChange={(e) => setNewItem({...newItem, year: e.target.value})} className={inputCls} style={{ color: '#15354F' }}>
                  <option value="">{t('profileMedHistory.selectYear')}</option>{yearOptions.map(y => <option key={y} value={y}>{y}</option>)}</select></div>
              <button onClick={handleAddCustomSurgery} disabled={!newItem.name.trim()}
                className={`w-full py-3 rounded-2xl font-bold text-[14px] ${newItem.name.trim() ? 'text-white' : 'bg-gray-200 text-gray-400'}`} style={newItem.name.trim() ? { background: '#15354F' } : {}}>{t('profileMedHistory.add')}</button>
            </div>
          </div>
        </div>
        </OverlayPortal>
      )}
    </>
  )
}
